/**
 * Catálogo comercial central — filtros y labels para solicitud / Centro SaaS.
 * Fuente: GET /system/info → module_catalog o mirror DEFAULT_MODULE_CATALOG.
 */
import { DEFAULT_MODULE_CATALOG, resolveModuleCatalog } from "@/modules/catalogModulos";

const COMMERCIAL_FLAG_DEFAULTS = {
  status: "active",
  visible_en_trial: false,
  visible_en_solicitud: false,
  visible_en_centro_saas: false,
  requiere_autorizacion: true,
};

/** @param {object} [systemInfo] */
export function resolveCommercialModuleCatalog(systemInfo) {
  return resolveModuleCatalog(systemInfo);
}

/** @param {object[]} catalog */
function sortedCatalog(catalog = []) {
  return [...(catalog || [])].sort(
    (a, b) => (a.checkbox_order ?? 99) - (b.checkbox_order ?? 99),
  );
}

/**
 * @param {object[]} catalog
 * @param {{ forTrial?: boolean, forRequest?: boolean, forSaas?: boolean, saasActiveOnly?: boolean }} [opts]
 */
export function filterCommercialModules(catalog = [], opts = {}) {
  const {
    forTrial = false,
    forRequest = false,
    forSaas = false,
    saasActiveOnly = true,
  } = opts;
  return sortedCatalog(catalog).filter((m) => {
    if (m.licensable === false) return false;
    const flags = { ...COMMERCIAL_FLAG_DEFAULTS, ...m };
    if (forTrial) {
      return m.enabled !== false && flags.visible_en_trial !== false;
    }
    if (forRequest) {
      return m.enabled !== false && flags.visible_en_solicitud !== false;
    }
    if (forSaas) {
      if (flags.visible_en_centro_saas === false) return false;
      if (saasActiveOnly && m.enabled === false) return false;
      return true;
    }
    return true;
  });
}

/** @param {object[]} catalog */
export function moduleIdsForTrial(catalog = DEFAULT_MODULE_CATALOG) {
  return filterCommercialModules(catalog, { forTrial: true }).map((m) => m.id);
}

/** @param {object[]} catalog */
export function moduleIdsForLicenseRequest(catalog = DEFAULT_MODULE_CATALOG) {
  return filterCommercialModules(catalog, { forRequest: true }).map((m) => m.id);
}

/** @param {object[]} catalog */
export function moduleIdsForSaasCenter(catalog = DEFAULT_MODULE_CATALOG) {
  return filterCommercialModules(catalog, { forSaas: true, saasActiveOnly: true }).map((m) => m.id);
}

/** @param {string} id @param {object[]} [catalog] */
export function moduleLabel(id, catalog = DEFAULT_MODULE_CATALOG) {
  const mid = String(id || "").trim();
  if (mid === "control_despacho") {
    return moduleLabel("despacho", catalog);
  }
  const row = (catalog || []).find((m) => m.id === mid);
  return row?.label || mid;
}

/** @param {Record<string, boolean>} modules @param {object[]} [catalog] */
export function formatModulesLabelFromDict(modules = {}, catalog = DEFAULT_MODULE_CATALOG) {
  const ids = moduleIdsForLicenseRequest(catalog);
  const merged = { ...(modules || {}) };
  if ("control_despacho" in merged && !("despacho" in merged)) {
    merged.despacho = merged.control_despacho;
  }
  return ids
    .filter((id) => merged[id])
    .map((id) => moduleLabel(id, catalog))
    .join(", ");
}

/** @param {object[]} catalog */
export function defaultRequestedModulesMap(catalog = DEFAULT_MODULE_CATALOG) {
  return Object.fromEntries(moduleIdsForLicenseRequest(catalog).map((id) => [id, false]));
}

/** @param {Record<string, boolean>} modules @param {object[]} catalog */
export function hasAtLeastOneCatalogModule(modules = {}, catalog = DEFAULT_MODULE_CATALOG) {
  const ids = moduleIdsForLicenseRequest(catalog);
  return ids.some((id) => Boolean(modules[id]));
}
